import { useQuery } from "@tanstack/react-query";
import { errorCode } from "./errors";
import { type RouterOutputs, useTRPC } from "./trpc";

/**
 * Accounts on a hosted server (BT_AUTH=accounts, ADR 0011). The desktop app and local development
 * have a single user: no sign-in, no user menu, no members, no admin interface.
 */

export type AuthMode = RouterOutputs["system"]["info"]["auth"];
export type Me = NonNullable<RouterOutputs["account"]["me"]>;

let mode: AuthMode = "none";

/** Set once at start from system.info. */
export function setAuthMode(value: AuthMode): void {
  mode = value;
}

export function authMode(): AuthMode {
  return mode;
}

export function hasAccounts(): boolean {
  return mode === "accounts";
}

/** Who is signed in (in any status), or null. */
export function useMe() {
  const trpc = useTRPC();
  return useQuery({ ...trpc.account.me.queryOptions(), staleTime: 30_000 });
}

/** The sign-in page, coming back to `next` (the current page) afterwards. */
export function signInUrl(next = window.location.pathname + window.location.search): string {
  return next.startsWith("/account")
    ? "/account/sign-in"
    : `/account/sign-in?next=${encodeURIComponent(next)}`;
}

/** A path from ?next= that stays within the app. */
export function safeNext(value: string | null): string {
  return value?.startsWith("/") && !value.startsWith("//") ? value : "/";
}

/**
 * With accounts: an expired session goes to the sign-in page, a disabled or deleted account to
 * the page that says so. Returns true when it took over.
 */
export function handleAccessError(error: unknown): boolean {
  if (!hasAccounts()) return false;
  const code = errorCode(error);
  const onAccountPage = window.location.pathname.startsWith("/account");
  if (code === "UNAUTHORIZED" && !onAccountPage) {
    window.location.assign(signInUrl());
    return true;
  }
  if ((code === "ACCOUNT_DISABLED" || code === "ACCOUNT_DELETED") && !onAccountPage) {
    window.location.assign("/account/restricted");
    return true;
  }
  return false;
}

/** "Jo Doe"; an account without a name shows its email. */
export function displayName(user: {
  firstName: string | null;
  lastName: string | null;
  email?: string | null;
}): string {
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || "";
}

/** "JD" for a picture-less avatar. */
export function initials(user: { firstName: string | null; lastName: string | null }): string {
  return [user.firstName, user.lastName]
    .map((part) => part?.trim()[0] ?? "")
    .join("")
    .toUpperCase();
}
